import {
  ChevronRight,
  FileText,
  FolderSearch,
  Globe,
  Laptop,
  Loader2,
  Pencil,
  Search,
  Terminal,
  Workflow,
  X,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { cn } from '../lib/cn';
import type { ToolCall } from '../lib/ws';

/**
 * Inline badge rendered between assistant-text chunks. Shows what
 * the agent is doing — name of the tool, a short summary of its
 * input, a spinner while it's pending, and the first lines of the
 * result once it comes back. Clicking expands the full payload.
 */
export function ToolCallBadge({ call, streaming }: { call: ToolCall; streaming: boolean }) {
  const done = call.result !== undefined;
  // Errors auto-expand so the user sees what went wrong without
  // hunting for the chevron.
  const [open, setOpen] = useState(!!call.isError);
  const meta = describeCall(call);
  const { icon } = meta;

  return (
    <div
      className={cn(
        'mc-menu-enter origin-top my-2 overflow-hidden rounded-lg border border-border bg-subtle/50 text-[13px]',
        call.isError && 'border-destructive/40 bg-destructive/5',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-11 w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-subtle"
      >
        <span className="shrink-0 text-muted-foreground">{icon}</span>
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium">{meta.verb}</span>{' '}
          <span className="text-muted-foreground">{meta.summary}</span>
        </span>
        {!done && streaming && (
          <Loader2 size={13} className="shrink-0 animate-spin text-muted-foreground" />
        )}
        {!done && !streaming && (
          <span className="shrink-0 text-xs text-muted-foreground">Прервано</span>
        )}
        {done && call.isError && <X size={13} className="shrink-0 text-destructive" />}
        <span className={cn('mc-chevron shrink-0 text-muted-foreground', open && 'rotate-90')}>
          <ChevronRight size={13} />
        </span>
      </button>
      {open && (
        <div className="mc-menu-enter origin-top overflow-hidden border-t border-border">
          <div className="px-3 py-2">
            {meta.detail && (
              <div className="mb-2 whitespace-pre-wrap font-mono text-[12px] text-muted-foreground">
                {meta.detail}
              </div>
            )}
            {done && (
              <details className="text-[12px]">
                <summary className="cursor-pointer select-none text-muted-foreground">
                  {call.isError ? 'Ошибка' : 'Результат'}
                </summary>
                <pre className="mt-1 max-h-[300px] overflow-auto whitespace-pre-wrap rounded bg-background/60 p-2 font-mono text-[11.5px] leading-relaxed">
                  {call.result}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type ToolMeta = {
  icon: ReactNode;
  verb: string;
  summary: string;
  detail?: string;
};

function asStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v == null) return '';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function firstNonEmpty(...xs: string[]): string {
  for (const x of xs) if (x) return x;
  return '';
}

function describeCall(call: ToolCall): ToolMeta {
  const input = (call.input ?? {}) as Record<string, unknown>;
  if (call.name.startsWith('mac_')) return describeMacCall(call.name, input);
  switch (call.name) {
    case 'Bash': {
      const cmd = asStr(input.command);
      return {
        icon: <Terminal size={14} />,
        verb: 'Терминал',
        summary: firstLine(cmd, 80),
        detail: cmd,
      };
    }
    case 'WebSearch': {
      const q = asStr(input.query);
      return {
        icon: <Globe size={14} />,
        verb: 'Поиск в сети',
        summary: q ? `"${truncate(q, 80)}"` : '',
        detail: q,
      };
    }
    case 'WebFetch': {
      const url = asStr(input.url);
      return {
        icon: <Globe size={14} />,
        verb: 'Страница',
        summary: truncate(url, 80),
        detail: url,
      };
    }
    case 'Read': {
      const p = asStr(input.file_path);
      return {
        icon: <FileText size={14} />,
        verb: 'Чтение',
        summary: p,
        detail: p,
      };
    }
    case 'Write': {
      const p = asStr(input.file_path);
      const body = asStr(input.content);
      return {
        icon: <Pencil size={14} />,
        verb: 'Запись',
        summary: p,
        detail: `${p}\n${truncate(body, 400)}`,
      };
    }
    case 'Edit':
    case 'MultiEdit': {
      const p = asStr(input.file_path);
      return {
        icon: <Pencil size={14} />,
        verb: call.name === 'Edit' ? 'Правка' : 'Правки',
        summary: p,
        detail: p,
      };
    }
    case 'Glob': {
      const pattern = asStr(input.pattern);
      return {
        icon: <FolderSearch size={14} />,
        verb: 'Поиск файлов',
        summary: pattern,
        detail: pattern,
      };
    }
    case 'Grep': {
      const pattern = asStr(input.pattern);
      const path = asStr(input.path);
      return {
        icon: <Search size={14} />,
        verb: 'Поиск в файлах',
        summary: firstNonEmpty(pattern, path),
        detail: `шаблон: ${pattern}${path ? `\nпуть: ${path}` : ''}`,
      };
    }
    case 'Task': {
      const t = asStr(input.description) || asStr(input.subagent_type);
      return {
        icon: <Workflow size={14} />,
        verb: 'Субагент',
        summary: t,
        detail: asStr(input.prompt),
      };
    }
    default:
      return {
        icon: <Terminal size={14} />,
        verb: call.name || 'Инструмент',
        summary: '',
        detail: asStr(call.input),
      };
  }
}

const MAC_VERBS: Record<string, string> = {
  mac_run_command: 'Мак · Терминал',
  mac_read_file: 'Мак · Чтение',
  mac_write_file: 'Мак · Запись',
  mac_list_files: 'Мак · Файлы',
  mac_make_dir: 'Мак · Новая папка',
  mac_move: 'Мак · Перемещение',
  mac_delete: 'Мак · Удаление',
  mac_system_info: 'Мак · Система',
};

/** «Мак» chat tools (server/src/mac-tools.ts) act on the owner's MacBook. */
function describeMacCall(name: string, input: Record<string, unknown>): ToolMeta {
  const verb = MAC_VERBS[name] ?? `Мак · ${name.slice(4)}`;
  const icon = <Laptop size={14} />;
  if (name === 'mac_run_command') {
    const cmd = asStr(input.command);
    const cwd = asStr(input.cwd);
    return {
      icon,
      verb,
      summary: firstLine(cmd, 80),
      detail: cwd
        ? `${cmd}
папка: ${cwd}`
        : cmd,
    };
  }
  if (name === 'mac_move') {
    const route = `${asStr(input.from)} → ${asStr(input.to)}`;
    return { icon, verb, summary: truncate(route, 80), detail: route };
  }
  if (name === 'mac_write_file') {
    const p = asStr(input.path);
    return {
      icon,
      verb,
      summary: p,
      detail: `${p}
${truncate(asStr(input.content), 400)}`,
    };
  }
  const p = asStr(input.path);
  return { icon, verb, summary: p, detail: p || undefined };
}

function firstLine(s: string, limit: number): string {
  const nl = s.indexOf('\n');
  const line = nl === -1 ? s : s.slice(0, nl);
  return truncate(line, limit);
}

function truncate(s: string, limit: number): string {
  if (s.length <= limit) return s;
  return `${s.slice(0, limit - 1)}…`;
}
